import { useNavigate } from "@entry/navigation";
import { useState } from "react";

import { ArrowUpRight, ChevronRight } from "@assets";
import { trumpContributionFrequencyLabel } from "@components/Goals/TrumpSidecar";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body, Caption, H2 } from "@components/Typography";
import { DEFAULT_RECURRING_AMOUNT } from "@constants";
import { Flow, IncomeAt18, Routes, TrumpContributionFrequency } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useGoalFlow, useModal, useTheme, useTrumpSidecar } from "@hooks";
import { Translations, i18n } from "@i18n";
import {
  calculateWithSidecar,
  calculateWithoutSidecar,
  convertCentsToDollar,
  convertDollarToCents,
  formatCurrency,
  formatCurrencyShorthand,
  getIncomeAt18Label,
  getPossessiveName,
  getSidecarInputs,
  getTaxBarWidthPercent,
} from "@utils";
import { AdjustBalanceModal } from "./AdjustBalanceModal";
import { AdjustContributionModal } from "./AdjustContributionModal";
import { AdjustIncomeModal } from "./AdjustIncomeModal";
import { useStyles } from "./styles";

const DEFAULT_BALANCE = 1_000;

export const TrumpSidecarTaxCalculationRoute = () => {
  const styles = useStyles();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { show } = useModal();

  const {
    goalProps: {
      flow,
      trumpAccountBalanceInCents,
      trumpContributionAmountInCents,
      trumpContributionFrequency,
      incomeAt18: incomeAt18FromGoal,
    },
    setGoalProps,
  } = useGoalFlow();

  const { firstName, isSelf, dateOfBirth, taxState } = useTrumpSidecar();

  const [accountBalance, setAccountBalance] = useState(
    trumpAccountBalanceInCents ? convertCentsToDollar(trumpAccountBalanceInCents) : DEFAULT_BALANCE,
  );
  const [contributionAmount, setContributionAmount] = useState(
    trumpContributionAmountInCents
      ? convertCentsToDollar(trumpContributionAmountInCents)
      : DEFAULT_RECURRING_AMOUNT,
  );
  const [contributionFrequency, setContributionFrequency] = useState(
    trumpContributionFrequency ?? TrumpContributionFrequency.Weekly,
  );
  const [incomeAt18, setIncomeAt18] = useState(incomeAt18FromGoal ?? IncomeAt18.FullTimeStudent);

  // Fall back to the first person copy when no name is available.
  const shouldUseSelfCopy = isSelf || !firstName;

  const sidecarInputs = getSidecarInputs(
    dateOfBirth,
    convertDollarToCents(contributionAmount),
    contributionFrequency,
    taxState,
    convertDollarToCents(accountBalance),
    incomeAt18,
  );

  const withoutSidecar = sidecarInputs ? calculateWithoutSidecar(sidecarInputs) : null;
  const withSidecar = sidecarInputs ? calculateWithSidecar(sidecarInputs) : null;

  // The projections are shown for the 20 year drawdown scenario (B2).
  const taxBill = withoutSidecar?.taxBillB2 ?? 0;
  const spendableWithout = withoutSidecar?.netSpendableB2 ?? 0;
  const spendableWith = withSidecar?.netSpendable ?? 0;

  const formattedTaxBill = formatCurrency(taxBill);
  const taxBarWidthPercent = getTaxBarWidthPercent(taxBill);

  const handleSubmit = () => {
    setGoalProps({
      trumpAccountBalanceInCents: convertDollarToCents(accountBalance),
      trumpContributionAmountInCents: convertDollarToCents(contributionAmount),
      trumpContributionFrequency: contributionFrequency,
      incomeAt18,
      expectedTaxBillWithoutSidecarInCents: convertDollarToCents(taxBill),
      expectedTaxBillWithSidecarInCents: 0,
      spendableWithoutSidecarInCents: convertDollarToCents(spendableWithout),
      spendableWithSidecarInCents: convertDollarToCents(spendableWith),
    });

    navigate(Routes.TrumpSidecarRecommendedContribution);
  };

  const title = shouldUseSelfCopy
    ? i18n.t(Translations.TrumpSidecarTaxCalculationRouteTitleSelf, {
        taxBill: formattedTaxBill,
      })
    : i18n.t(Translations.TrumpSidecarTaxCalculationRouteTitle, {
        firstName,
        taxBill: formattedTaxBill,
      });

  // TextHighlighter matches on word boundaries, which never match a leading
  // "$", so the tax bill is emphasised directly here.
  const [titleBeforeTaxBill, ...titleAfterTaxBill] = title.split(formattedTaxBill);
  const highlightedTitle = titleAfterTaxBill.length ? (
    <H2>
      {titleBeforeTaxBill}
      <span style={{ color: theme.goal.sidecar.negative }}>{formattedTaxBill}</span>
      {titleAfterTaxBill.join(formattedTaxBill)}
    </H2>
  ) : (
    <H2>{title}</H2>
  );

  const props: OnboardingLayoutProps = {
    hideLogout: flow !== Flow.Onboarding,
    cta: {
      primary: {
        title: shouldUseSelfCopy
          ? i18n.t(Translations.TrumpSidecarTaxCalculationRoutePrimaryCTASelf)
          : i18n.t(Translations.TrumpSidecarTaxCalculationRoutePrimaryCTA, {
              possessiveFirstName: getPossessiveName(firstName),
            }),
        onClick: handleSubmit,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <Spacing size={SpacingSizes.M} />

      {highlightedTitle}
      <Spacing size={SpacingSizes.M} />

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <Body style={styles.cardLabel}>
            {i18n.t(Translations.TrumpSidecarTaxCalculationRouteWithoutSidecar)}
          </Body>
          <div style={styles.badge}>
            <Caption style={styles.badgeText}>
              {i18n.t(Translations.TrumpSidecarTaxCalculationRouteSpendable, {
                amount: formatCurrencyShorthand(spendableWithout),
              })}
            </Caption>
          </div>
        </div>

        <div style={styles.amountRow}>
          <div style={{ ...styles.taxBar, width: `${taxBarWidthPercent}%` }}></div>
          <Body style={styles.negativeAmount}>{formattedTaxBill}</Body>
        </div>

        <Caption style={styles.caption}>
          {i18n.t(Translations.TrumpSidecarTaxCalculationRouteExpectedTaxBillWithout)}
        </Caption>
      </div>
      <Spacing size={SpacingSizes.S} />

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <Body style={styles.cardLabel}>
            {i18n.t(Translations.TrumpSidecarTaxCalculationRouteWithSidecar)}
          </Body>
          <div style={styles.badge}>
            <ArrowUpRight fill={theme.goal.sidecar.positive} />
            <Caption
              style={{
                ...styles.badgeText,
                color: theme.goal.sidecar.positive,
              }}
            >
              {i18n.t(Translations.TrumpSidecarTaxCalculationRouteSpendable, {
                amount: formatCurrencyShorthand(spendableWith),
              })}
            </Caption>
          </div>
        </div>

        <div style={styles.amountRow}>
          <div style={styles.positiveBar}></div>
          <Body style={styles.positiveAmount}>{formatCurrency(0)}</Body>
        </div>

        <Caption style={styles.caption}>
          {i18n.t(Translations.TrumpSidecarTaxCalculationRouteExpectedTaxBillWith)}
        </Caption>
      </div>
      <Spacing size={SpacingSizes.M} />

      <div style={styles.inputRow}>
        <div
          style={styles.inputCard}
          onClick={() =>
            show(
              <AdjustBalanceModal currentBalance={accountBalance} onUpdate={setAccountBalance} />,
            )
          }
        >
          <div style={styles.inputLabelRow}>
            <Caption style={styles.inputLabel}>
              {i18n.t(Translations.TrumpSidecarTaxCalculationRouteBalanceLabel)}
            </Caption>
            <ChevronRight />
          </div>
          <Body style={styles.inputValue}>{formatCurrency(accountBalance)}</Body>
        </div>

        <div
          style={styles.inputCard}
          onClick={() =>
            show(
              <AdjustContributionModal
                currentAmount={contributionAmount}
                currentFrequency={contributionFrequency}
                onUpdate={(amount, frequency) => {
                  setContributionAmount(amount);
                  setContributionFrequency(frequency);
                }}
              />,
            )
          }
        >
          <div style={styles.inputLabelRow}>
            <Caption style={styles.inputLabel}>
              {i18n.t(Translations.TrumpSidecarTaxCalculationRouteContributionLabel)}
            </Caption>
            <ChevronRight />
          </div>
          <Body style={styles.inputValue}>
            {`${formatCurrency(contributionAmount)} (${trumpContributionFrequencyLabel[
              contributionFrequency
            ].toLowerCase()})`}
          </Body>
        </div>
      </div>
      <Spacing size={SpacingSizes.S} />

      <div
        style={styles.inputCard}
        onClick={() =>
          show(<AdjustIncomeModal currentIncome={incomeAt18} onUpdate={setIncomeAt18} />)
        }
      >
        <div style={styles.inputLabelRow}>
          <Caption style={styles.inputLabel}>
            {i18n.t(Translations.TrumpSidecarTaxCalculationRouteIncomeLabel)}
          </Caption>
          <ChevronRight />
        </div>
        <Body style={styles.inputValue}>{getIncomeAt18Label(incomeAt18)}</Body>
      </div>
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
